import { useTranslation } from 'react-i18next';
import { useEditorNavigate, useRequiredParams, useEditorSearch, useIsViewingVersion } from 'app/router/params';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import React, { useCallback, useMemo, useState } from 'react';
import { useSlate } from 'slate-react';
import moment from 'moment';
import { api } from 'shared/api/client';
import { i18n } from 'shared/i18n';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { useCreateVersion, documentKeys, SectionEntry, SectionTargetLanguage } from 'entities/document';
import { NavbarPortal } from 'app/components';
import logo from 'app/images/Xplir_logo_icon.svg';
import { useDiff } from 'app/state/contexts/DiffContext';
import { useSidebar } from 'app/state/contexts/SidebarContext';
import { useUser } from 'app/state/contexts/UserContext';

import { FileZone } from 'app/components';
import { v4 as uuidv4 } from 'uuid';
import { LazyTippy, Modal } from 'app/components';
import DraftMark from './DraftMark';
import { useComments } from 'entities/comment';
import Drawer from 'react-modern-drawer';
import { createPortal } from 'react-dom';
import 'react-modern-drawer/dist/index.css';
import { NotificationsDrawer } from './NotificationsDrawer';
import { usePermissions } from 'app/api/permission-queries';
import { useSimpleVersions } from 'entities/document';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorCoarse } from 'app/state/contexts/EditorUiContext';
import { useEditorMode } from 'app/state/contexts/EditorContext';

interface NavComment {
  message?: string;
  closedAt?: string | null;
  publishedAt?: string | null;
  onType?: string;
  [key: string]: unknown;
}

interface VersionItem {
  documentRevisionId?: string;
  name?: string;
  [key: string]: unknown;
}

const getDocumentSaveSince = (lastSaved: string | Date | null | undefined = null, isDirty: boolean) => {
  if (!lastSaved) {
    return '';
  }
  const now = moment();
  const d = moment(lastSaved);

  const diff = now.diff(d, 'minutes');

  if (diff === 0 && isDirty) {
    return 'Sparar ...';
  } else if (diff === 0) {
    const diffSec = now.diff(d, 'seconds');
    return `Sparat ${diffSec > 10 ? 'för ' + diffSec + ' sekunder sedan' : ''}`;
  } else if (diff < 60) {
    return `Sparades för ${diff} minuter sedan`;
  } else if (diff < 24 * 60) {
    return `Sparades för ${Math.round(diff / 60)} timmar sedan`;
  } else {
    return `Sparades för ${Math.round(diff / (24 * 60))} dagar sedan`;
  }
};

function versionNamedOrSaveStatus(selectedVersionName: string | undefined, createdAt: string | Date | null | undefined, isDirty: boolean) {
  return selectedVersionName ? `${i18n('doc-editor:navbar.version')}: ${selectedVersionName}` : getDocumentSaveSince(createdAt, isDirty);
}

interface SafeNavButtonProps {
  isDirty: boolean;
  prevLink?: string;
  documentId: string;
}

const SafeNavButton = ({ isDirty, prevLink, documentId }: SafeNavButtonProps) => {
  const { t } = useTranslation('document');

  const translationId = useEditorSearch().translation;
  const navigate = useEditorNavigate();
  const handleGoBack = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isDirty) {
      window.alert(t('not-saved.warning-message'));
      if (translationId !== null) {
        navigate({ search: (prev) => ({ ...prev, translation: translationId }) });
      } else {
        navigate({ search: (prev) => ({ ...prev, translation: undefined }) });
      }
    } else {
      window.location.reload();
    }
  };

  if (isDirty) {
    return (
      <a className="navigation" href="#" onClick={handleGoBack}>
        <button className="button--small clear">
          <i className="fa-regular fa-chevron-left" />
        </button>
      </a>
    );
  }
  return (
    <Link className="navigation" to={prevLink || `/documents/${documentId}`}>
      <button className="button--small clear">
        <i className="fa-regular fa-chevron-left" />
      </button>
    </Link>
  );
};

interface NavbarProps {
  showXliff?: boolean;
  prevLink?: string;
  readOnly?: boolean;
  isDraft?: boolean;
}

const Navbar = React.memo(({ showXliff = true, prevLink = '', readOnly = false, isDraft = false }: NavbarProps) => {
  const navigate = useEditorNavigate();
  const queryClient = useQueryClient();
  const editor = useSlate();
  const isViewingVersion = useIsViewingVersion();
  const { user, organizations, selectedOrganization } = useUser();
  const { setIsSidebarOpen } = useSidebar();
  const { documentId, sectionId } = useRequiredParams('documentId', 'sectionId');
  const { diffState } = useDiff();
  const [focusedDiff, setFocusedDiff] = useState(0);
  const { comments, isCommentsLoading } = useComments(sectionId) as { comments: NavComment[] | undefined; isCommentsLoading: boolean };
  const selectedTranslation = useEditorSearch().translation;
  const { isTaggingMode } = useEditorMode();
  const org = organizations?.find((o: { id: number }) => o.id === selectedOrganization);
  const { documentPermissions: reduxDocumentPermissions, sectionPermissions: reduxSectionPermissions } = usePermissions(documentId);
  const isDirty = useEditorCoarse().isDirty;
  const { data: pDocument } = useEditorDocument();
  const documentSuperId = pDocument?.superId;
  const documentRevisionId = pDocument?.revisionId;
  const documentName = pDocument?.name;
  const documentCreatedAt = pDocument?.createdAt;
  const documentLocale = pDocument?.content?.locale;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const currentSection = pDocument?.content?.sections?.find((section: SectionEntry) => section.superId === sectionId);
  const { data: versions } = useSimpleVersions(documentId);
  const selectedVersionName = (versions as VersionItem[] | undefined)?.find((version) => version.documentRevisionId === documentRevisionId)?.name;
  const targetLanguages = currentSection?.targetLanguages;
  const [versionName, setVersionName] = useState('');
  const [errorName, setErrorName] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const mentionedComments =
    comments?.filter((comment) => comment?.message?.includes(`@${user?.firstName}${user?.lastName}`) && !comment.closedAt && comment.publishedAt) ?? [];

  const commentCount = useMemo(() => {
    if (isCommentsLoading || !comments) return 0;
    return comments.filter((comment) => comment.publishedAt !== null && comment.closedAt === null && comment.onType !== 'comment').length;
  }, [comments, isCommentsLoading]);

  const { mutate: createVersion } = useCreateVersion();

  const handleSubmitVersion = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validName(versionName)) {
      setErrorName(true);
      return;
    }

    createVersion(
      { documentId, documentSuperId, documentRevisionId, name: versionName },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: documentKeys.simpleVersions(documentId) });
          setModalOpen(false);
          setVersionName('');
        },
      }
    );
  };

  function validName(name: string) {
    return name.trim().length > 0 && name.trim().length < 65;
  }

  const handleVersionNameUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVersionName(event.target.value);
  };
  const xliff = useCallback(
    (_e: React.MouseEvent) => {
      api
        .GET('/sections/xliff/{revisionId}', {
          params: {
            path: { revisionId: currentSection?.revisionId as string },
            query: selectedTranslation ? { target: selectedTranslation } : {},
          },
        })
        .then((res) => res.data)
        .then((data: { xliff: string }) => {
          const url = window.URL.createObjectURL(new Blob([data.xliff], { type: 'text/xml' }));

          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `translation${'_' + currentSection?.name + '_(' + moment(new Date()).format('YYYYMMDD_HHmm') + ')'}.xliff`);

          // Append to html link element page
          document.body.appendChild(link);

          // Start download
          link.click();

          // Clean up and remove the link
          link.parentNode?.removeChild(link);
        });
    },
    [currentSection, selectedTranslation]
  );

  const handleStepChange = (step: number) => () => {
    setFocusedDiff((focusedDiff + step) % Object.keys(diffState?.right ?? {}).length);
  };

  const handleLangChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (event.target.value.toString().trim() !== '') {
      navigate({ search: (prev) => ({ ...prev, translation: event.target.value.toString() }) });
    } else {
      navigate({ search: (prev) => ({ ...prev, translation: undefined }) });
    }
  };

  const handleCloseModal = () => {
    setVersionName('');
    setErrorName(false);
    setModalOpen(false);
  };

  const onUploadSuccess = async ({ data }: { data: { revisionId: string; filename: string } }) => {
    const locale = (currentSection?.locale ?? documentLocale) === 'sv-SE' ? 'en-US' : 'sv-SE';
    const replacesOpenTranslation = targetLanguages?.some((t: SectionTargetLanguage) => t.superId === selectedTranslation && t.locale === locale);
    try {
      const { data: translatedSection } = await api.GET('/sections/xliff/{revisionId}/{fileId}', {
        params: { path: { revisionId: currentSection?.revisionId as string, fileId: data.revisionId } },
      });
      const { data: section } = await api.POST('/sections/', {
        body: {
          superId: uuidv4(),
          revisionId: uuidv4(),
          content: translatedSection.content,
          documentSuperId: Number(documentId),
        },
      });
      await api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentId) } },
        body: {
          action: 'create_section_target_lang',
          revisionId: currentSection?.revisionId,
          data: {
            superId: currentSection?.superId,
            targetLanguage: {
              locale,
              name: data.filename + ' (' + moment(new Date()).format('YYYY-MM-DD HH:mm') + ')',
              superId: section.superId,
              revisionId: section.revisionId,
            },
            pageIdMappings: translatedSection.pageIdMappings,
          },
        },
      });
      await queryClient.invalidateQueries({ queryKey: documentKeys.detailPrefix(documentId) });
      if (replacesOpenTranslation) {
        navigate({ search: (prev) => ({ ...prev, translation: section.superId }) });
      }
      dispatchMessage(EventType.ADD_MESSAGE, { type: 'success', text: i18n('doc-editor:navbar.xliff-import.success'), removeTime: 4000 });
    } catch (e) {
      console.error(e);
      dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: i18n('doc-editor:navbar.xliff-import.error'), removeTime: 6000 });
    }
  };
  return (
    <>
      <NavbarPortal fullWidth>
        <div className="navbar--editor">
          <div className="logo">
            <img alt="logo" src={logo} />
          </div>
          <SafeNavButton isDirty={isDirty} documentId={documentId} prevLink={prevLink} />
          <div className="project">
            <Link className="me-3" to="/documents/$documentId" params={{ documentId }}>
              <p className="sub-heading mb-1">{org?.name || ''}</p>
              <div className="d-flex align-items-center">
                <h4>{documentName}</h4>
                <span className="ms-3 text-gray-900 fs-7">
                  {versionNamedOrSaveStatus(selectedVersionName, documentCreatedAt, isDirty)}
                  {isDirty && '*'}
                </span>
              </div>
            </Link>
          </div>
          <div className="controls">
            {diffState && (
              <>
                <button className="button--small clear" onClick={handleStepChange(1)}>
                  <i className="fa-regular fa-arrow-down-long" />
                </button>
                <button className="button--small clear" onClick={handleStepChange(-1)}>
                  <i className="fa-regular fa-arrow-up-long" />
                </button>
              </>
            )}
            {!readOnly && (
              <>
                <button
                  className="button--small clear"
                  onClick={() => {
                    editor.undo();
                  }}
                >
                  <i className="fa-regular fa-arrow-rotate-left" />
                </button>
                <button
                  className="button--small clear"
                  onClick={() => {
                    editor.redo();
                  }}
                >
                  <i className="fa-regular fa-arrow-rotate-right" />
                </button>
              </>
            )}
            {isDraft ? (
              <DraftMark />
            ) : (
              <>
                <div className="version-icon" onClick={() => setIsSidebarOpen(5)}>
                  {commentCount}
                  <i className="fa-regular fa-message nav-btn-icon"></i>
                </div>

                <Link
                  to="/sbs/documents/$documentId/sections/$sectionId"
                  params={{ documentId, sectionId }}
                  className="navbar-link-group"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="button grey200">{i18n('doc-editor:navbar.compare-versions')}</div>
                </Link>
                {!isViewingVersion &&
                (reduxDocumentPermissions.includes('ADMIN') || Object.values(reduxSectionPermissions).some((p: string) => p === 'WRITE ')) ? (
                  <div className="button grey200" onClick={() => setModalOpen(true)}>
                    {i18n('doc-editor:navbar.new-version')}
                    <i className="fa-regular fa-floppy-disk nav-btn-icon" />
                  </div>
                ) : null}
                {showXliff && (
                  <LazyTippy
                    interactive={!isDirty}
                    trigger={'click'}
                    content={
                      <div className="card support-modal">
                        <div className="button grey200" onClick={(e) => xliff(e)}>
                          {i18n('doc-editor:navbar.xliff')}
                          <i className="fa-regular fa-floppy-disk nav-btn-icon" />
                        </div>
                        {isTaggingMode && targetLanguages ? (
                          <p className="small text-muted mb-1">{i18n('doc-editor:navbar.translation.blocked-in-tagging')}</p>
                        ) : null}
                        {targetLanguages ? (
                          <select onChange={handleLangChange} value={selectedTranslation ? selectedTranslation : ''} disabled={isTaggingMode}>
                            {targetLanguages.map((_translation: SectionTargetLanguage) => {
                              return (
                                <option key={_translation.superId} value={_translation.superId}>
                                  {_translation.name + ' (' + _translation.locale + ')'}
                                </option>
                              );
                            })}
                            <option key="org" value="">
                              Orginalspråk
                            </option>
                          </select>
                        ) : (
                          ''
                        )}
                        {!isViewingVersion && <FileZone type="xliff" documentId={documentId} onSuccess={onUploadSuccess} />}
                      </div>
                    }
                  >
                    <div className="button grey200">{i18n('doc-editor:navbar.translation')}</div>
                  </LazyTippy>
                )}
              </>
            )}
            <div className="navbar-user-icon-wrapper">
              <div className="user--circle">
                {user?.firstName?.[0]?.toUpperCase()}
                {user?.lastName?.[0]?.toUpperCase()}
              </div>
            </div>
            <div className="navbar-notification-icon-wrapper" onClick={() => setIsDrawerOpen(true)}>
              <i className="fa-regular fa-bell"></i>
              {mentionedComments.length > 0 && <p className="navbar-notification-count-icon">{mentionedComments.length}</p>}
            </div>
          </div>
        </div>
      </NavbarPortal>
      <Modal show={modalOpen}>
        <div className="header d-flex">
          <h3>{i18n('project-details:new-version.modal.title')}</h3>
          <div className="close" onClick={handleCloseModal}>
            <i className="fa-regular fa-xmark" />
          </div>
        </div>
        <div className="body">
          <form onSubmit={handleSubmitVersion} className={errorName ? 'form with-footer error' : 'form with-footer'}>
            <div className="mt-4">
              <label>
                <input type="text" className="form-control" ref={(o) => o?.focus()} placeholder="" value={versionName} onChange={handleVersionNameUpdate} />
              </label>
              <span className="text-help">{errorName ? i18n('overview:new-version.modal.field.nameError') : ''}</span>
            </div>
            <div className="d-flex justify-content-end">
              <button type="button" className="button clear" onClick={handleCloseModal}>
                {i18n('common:button.cancel')}
              </button>
              <button type="submit" className="button primary">
                {i18n('common:button.save')}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {createPortal(
        <Drawer open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} direction="right" size={450}>
          <NotificationsDrawer onClose={() => setIsDrawerOpen(false)} />
        </Drawer>,
        document.body,
        'notifications-drawer'
      )}
    </>
  );
});

export default Navbar;
